/**
 * HR employee record. Deliberately separate from the Prism `User` model: most employees in the
 * org chart never have a Prism/fishbowl login. `userId` optionally links the two when they do.
 */
export enum EmployeeStatus {
    Active = 'active',
    OnLeave = 'on-leave',
    Terminated = 'terminated',
    Resigned = 'resigned',
    Retired = 'retired',
}

export const EMPLOYEE_STATUS_LABELS: Record<EmployeeStatus, string> = {
    [EmployeeStatus.Active]: 'Active',
    [EmployeeStatus.OnLeave]: 'On Leave',
    [EmployeeStatus.Terminated]: 'Terminated',
    [EmployeeStatus.Resigned]: 'Resigned',
    [EmployeeStatus.Retired]: 'Retired',
};

/** Statuses that count as "still with the company" for the org chart. */
export const EMPLOYED_STATUSES: readonly EmployeeStatus[] = [EmployeeStatus.Active, EmployeeStatus.OnLeave];

export enum EmploymentType {
    FullTime = 'full-time',
    PartTime = 'part-time',
    Temporary = 'temporary',
    Contractor = 'contractor',
}

export const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
    [EmploymentType.FullTime]: 'Full Time',
    [EmploymentType.PartTime]: 'Part Time',
    [EmploymentType.Temporary]: 'Temporary',
    [EmploymentType.Contractor]: 'Contractor',
};

export enum EmployeeShift {
    First = 'first',
    Second = 'second',
    Third = 'third',
}

export const EMPLOYEE_SHIFT_LABELS: Record<EmployeeShift, string> = {
    [EmployeeShift.First]: '1st shift',
    [EmployeeShift.Second]: '2nd shift',
    [EmployeeShift.Third]: '3rd shift',
};

export interface Employee {
    id: string;
    // identity
    firstName: string;
    lastName: string;
    preferredName: string;
    imageUrl: string;
    thumbnailImageUrl: string;
    // org placement
    jobTitle: string;
    managerEmployeeId: string | null;
    /** Linked Prism user when the employee has a login; null otherwise. */
    userId: string | null;
    // lifecycle
    status: EmployeeStatus;
    employmentType: EmploymentType;
    shift: EmployeeShift | null;
    hireDate: Date;
    endDate: Date | null;
    endReason: string;
    // contact
    workEmail: string;
    workPhone: string;
    personalEmail: string;
    personalPhone: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    postalCode: string;
    // emergency contact
    emergencyContactName: string;
    emergencyContactPhone: string;
    emergencyContactRelationship: string;
    dateOfBirth: Date | null;
    notes: string;
    // audit
    createdOn: Date;
    createdById: string;
    updatedOn: Date;
    updatedById: string;
}

export const DEFAULT_EMPLOYEE: Employee = {
    id: '',
    firstName: '',
    lastName: '',
    preferredName: '',
    imageUrl: '',
    thumbnailImageUrl: '',
    jobTitle: '',
    managerEmployeeId: null,
    userId: null,
    status: EmployeeStatus.Active,
    employmentType: EmploymentType.FullTime,
    shift: null,
    hireDate: new Date(),
    endDate: null,
    endReason: '',
    workEmail: '',
    workPhone: '',
    personalEmail: '',
    personalPhone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
    dateOfBirth: null,
    notes: '',
    createdOn: new Date(),
    createdById: '',
    updatedOn: new Date(),
    updatedById: '',
};

export function employeeDisplayName(employee: Pick<Employee, 'firstName' | 'lastName' | 'preferredName'>): string {
    const first = employee.preferredName?.trim() || employee.firstName?.trim() || '';
    return `${first} ${employee.lastName?.trim() || ''}`.trim();
}

export function employeeInitials(employee: Pick<Employee, 'firstName' | 'lastName' | 'preferredName'>): string {
    const first = employee.preferredName?.trim() || employee.firstName?.trim() || '';
    const last = employee.lastName?.trim() || '';
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

export function isEmployedStatus(status: EmployeeStatus): boolean {
    return EMPLOYED_STATUSES.includes(status);
}

/** Sort used for siblings in the chart and for list views: last name, then first name. */
export function compareEmployees(a: Employee, b: Employee): number {
    const byLast = (a.lastName || '').localeCompare(b.lastName || '');
    if (byLast !== 0) {
        return byLast;
    }
    return (a.firstName || '').localeCompare(b.firstName || '');
}

export interface OrgChartNode {
    employee: Employee;
    children: OrgChartNode[];
}

/**
 * Builds the reports-to tree. Roots are employees with no manager or whose manager is not in the
 * given list. Employees caught in a manager cycle (A -> B -> A) are surfaced as additional roots so
 * nobody silently disappears from the chart.
 */
export function buildOrgTree(employees: Employee[]): OrgChartNode[] {
    const byId = new Map<string, Employee>(employees.map(employee => [employee.id, employee]));
    const childrenByManager = new Map<string, Employee[]>();
    const roots: Employee[] = [];

    for (const employee of employees) {
        const managerId = employee.managerEmployeeId;
        if (!managerId || managerId === employee.id || !byId.has(managerId)) {
            roots.push(employee);
            continue;
        }
        const siblings = childrenByManager.get(managerId) ?? [];
        siblings.push(employee);
        childrenByManager.set(managerId, siblings);
    }

    const visited = new Set<string>();
    const toNode = (employee: Employee): OrgChartNode => {
        visited.add(employee.id);
        const children = (childrenByManager.get(employee.id) ?? [])
            .filter(child => !visited.has(child.id))
            .sort(compareEmployees)
            .map(toNode);
        return { employee, children };
    };

    const tree = roots.sort(compareEmployees).map(toNode);

    // Anything unreached is part of a cycle: promote the first unvisited member to a root.
    for (const employee of [...employees].sort(compareEmployees)) {
        if (!visited.has(employee.id)) {
            tree.push(toNode(employee));
        }
    }

    return tree;
}

/** Every employee below `employeeId` in the reports-to tree (used to stop manager cycles). */
export function descendantEmployeeIds(employeeId: string, employees: Employee[]): Set<string> {
    const childrenByManager = new Map<string, string[]>();
    for (const employee of employees) {
        if (!employee.managerEmployeeId) {
            continue;
        }
        const ids = childrenByManager.get(employee.managerEmployeeId) ?? [];
        ids.push(employee.id);
        childrenByManager.set(employee.managerEmployeeId, ids);
    }

    const descendants = new Set<string>();
    const stack = [...(childrenByManager.get(employeeId) ?? [])];
    while (stack.length) {
        const id = stack.pop() as string;
        if (descendants.has(id) || id === employeeId) {
            continue;
        }
        descendants.add(id);
        stack.push(...(childrenByManager.get(id) ?? []));
    }
    return descendants;
}

/**
 * Returns the employee with a status change applied. Separations stamp `endDate`/`endReason`;
 * returning to an employed status clears them.
 */
export function applyEmployeeStatusChange(
    employee: Employee,
    status: EmployeeStatus,
    effectiveDate: Date,
    reason: string,
): Employee {
    if (isEmployedStatus(status)) {
        return { ...employee, status, endDate: null, endReason: '' };
    }
    return { ...employee, status, endDate: effectiveDate, endReason: reason.trim() };
}
